import { AvatarCatalog } from "@/game/avatar/catalog";
import { PowerupDefinition } from "../types/powerups";

export const POWERUPS: Record<string, PowerupDefinition> = {
  finger_guns: {
    id: "finger_guns",
    name: "Finger Guns",
    description: "Increase likes gained per click.",
    icon: "mouse",
    category: "click",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 10,
      factor: 1.15,
    },
    effects: [{ type: "add_economy", target: "likesPerClick", amount: 1 }],
    tags: ["likesPerClick"],
  },

  double_tap: {
    id: "double_tap",
    name: "Double Tap",
    description: "Multiply click strength.",
    icon: "heart",
    category: "click",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 50,
      factor: 1.22,
    },
    unlockRequirements: [
      { type: "powerup_level", powerupId: "finger_guns", level: 5 },
    ],
    effects: [
      { type: "multiply_economy", target: "likesPerClick", amount: 0.25 },
    ],
    tags: ["clickMultiplier"],
  },

  viewbot: {
    id: "viewbot",
    name: "ViewBot",
    description: "Add permanent botted viewers.",
    icon: "bot",
    category: "audience",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 25,
      factor: 1.18,
    },
    effects: [{ type: "add_economy", target: "bottedViewers", amount: 6 }],
    tags: ["bottedViewers"],
  },

  viewbot_farm: {
    id: "viewbot_farm",
    name: "ViewBot Farm",
    description: "Add even more permanent botted viewers per purchase.",
    icon: "users",
    category: "audience",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 200,
      factor: 1.24,
    },
    unlockRequirements: [
      { type: "powerup_level", powerupId: "viewbot", level: 10 },
    ],
    effects: [{ type: "add_economy", target: "bottedViewers", amount: 30 }],
    tags: ["bottedViewers"],
  },

  engagement_bait: {
    id: "engagement_bait",
    name: "Engagement Bait",
    description: "Increase likes generated from total viewers.",
    icon: "sparkles",
    category: "conversion",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 75,
      factor: 1.2,
    },
    unlockRequirements: [
      { type: "powerup_level", powerupId: "viewbot", level: 3 },
    ],
    effects: [
      {
        type: "multiply_economy",
        target: "likesPerViewerPerSecond",
        amount: 0.06,
      },
    ],
    tags: ["likesPerViewer"],
  },

  stream_title: {
    id: "stream_title",
    name: "Stream Title",
    description: "Multiply passive likes production.",
    icon: "camera",
    category: "conversion",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 120,
      factor: 1.23,
    },
    unlockRequirements: [{ type: "likes", amount: 100 }],
    effects: [
      { type: "unlock_stream_title" },
      {
        type: "multiply_economy",
        target: "likesPerSecondMultiplier",
        amount: 0.12,
      },
    ],
    tags: ["likesPerSecondMultiplier"],
  },

  lofi_radio: {
    id: "lofi_radio",
    name: "Lo-Fi Radio",
    description:
      "Unlock a looping lo-fi track that improves vibe, retention, and real viewer flow.",
    icon: "sparkles",
    category: "content",
    kind: "unlock",
    scaling: {
      method: "flat",
      baseCost: 260,
    },
    maxLevel: 1,
    unlockRequirements: [{ type: "likes", amount: 180 }],
    effects: [
      { type: "unlock_music", musicId: "lofi" },
      {
        type: "add_economy",
        target: "realViewerFlowPerSecond",
        amount: 0.34,
      },
      {
        type: "add_economy",
        target: "realViewerRetention",
        amount: 0.18,
      },
    ],
    tags: ["music", "lofi", "realViewerFlow", "retention"],
  },

  camera_ready_smile: {
    id: "camera_ready_smile",
    name: "Camera Ready Smile",
    description:
      "Unlock a charming face that steadily pulls in and keeps more real viewers.",
    icon: "sparkles",
    category: "content",
    kind: "unlock",
    scaling: {
      method: "flat",
      baseCost: 90,
    },
    maxLevel: 1,
    unlockRequirements: [{ type: "likes", amount: 45 }],
    effects: [
      {
        type: "set_avatar_slots",
        slots: {
          eyes: "skin/expression/eyes/grinEye",
          mouth: "skin/expression/mouth/grinMouth",
        },
      },
      {
        type: "add_real_viewers",
        amount: 2,
      },
      {
        type: "add_economy",
        target: "realViewerFlowPerSecond",
        amount: 0.4,
      },
      {
        type: "add_economy",
        target: "realViewerRetention",
        amount: 0.25,
      },
    ],
    tags: ["appearance", "realViewers", "realViewerFlow", "retention"],
  },

  soft_blush: {
    id: "soft_blush",
    name: "UwU",
    description:
      "A softer look improves retention and helps more real viewers stick around.",
    icon: "heart",
    category: "content",
    kind: "unlock",
    scaling: {
      method: "flat",
      baseCost: 140,
    },
    maxLevel: 1,
    unlockRequirements: [
      { type: "powerup_level", powerupId: "camera_ready_smile", level: 1 },
    ],
    effects: [
      {
        type: "set_avatar_slots",
        slots: {
          blush: "skin/expression/blush",
        },
      },
      {
        type: "add_real_viewers",
        amount: 1,
      },
      {
        type: "add_economy",
        target: "realViewerFlowPerSecond",
        amount: 0.3,
      },
      {
        type: "add_economy",
        target: "realViewerRetention",
        amount: 0.2,
      },
    ],
    tags: ["appearance", "realViewers", "realViewerFlow", "retention"],
  },

  signature_hair: {
    id: "signature_hair",
    name: "Signature Hair",
    description:
      "A recognizable style boosts discoverability and keeps more real people around.",
    icon: "shirt",
    category: "content",
    kind: "unlock",
    scaling: {
      method: "flat",
      baseCost: 220,
    },
    maxLevel: 1,
    unlockRequirements: [
      { type: "powerup_level", powerupId: "camera_ready_smile", level: 1 },
    ],
    effects: [
      {
        type: "set_avatar_slots",
        slots: {
          hair: "skin/hair/hair",
        },
      },
      {
        type: "add_real_viewers",
        amount: 3,
      },
      {
        type: "add_economy",
        target: "realViewerFlowPerSecond",
        amount: 0.5,
      },
      {
        type: "add_economy",
        target: "realViewerRetention",
        amount: 0.25,
      },
    ],
    tags: ["appearance", "realViewers", "realViewerFlow", "retention"],
  },

  unlock_backflip: {
    id: "unlock_backflip",
    name: "Backflip",
    description: "Unlock the Backflip animation.",
    icon: "rotate-cw",
    category: "animation",
    kind: "unlock",
    scaling: {
      method: "flat",
      baseCost: 150,
    },
    maxLevel: 1,
    animationId: "Backflip",
    unlockRequirements: [{ type: "likes", amount: 75 }],
    effects: [{ type: "unlock_animation", animationId: "Backflip" }],
    tags: ["unlockAnimation"],
  },

  backflip_hype: {
    id: "backflip_hype",
    name: "Backflip Hype",
    description: "Increase real viewers gained from Backflip.",
    icon: "zap",
    category: "animation",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 175,
      factor: 1.2,
    },
    animationId: "Backflip",
    unlockRequirements: [
      { type: "animation_unlocked", animationId: "Backflip" },
    ],
    effects: [
      {
        type: "multiply_animation_power",
        animationId: "Backflip",
        amount: 0.2,
      },
    ],
    tags: ["animationPower"],
  },

  backflip_cdr: {
    id: "backflip_cdr",
    name: "Backflip Cooldown",
    description: "Reduce Backflip cooldown.",
    icon: "rotate-cw",
    category: "animation",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 225,
      factor: 1.24,
    },
    animationId: "Backflip",
    unlockRequirements: [
      { type: "animation_unlocked", animationId: "Backflip" },
    ],
    effects: [
      {
        type: "multiply_animation_cooldown",
        animationId: "Backflip",
        amount: 0.08,
        minMultiplier: 0.35,
      },
    ],
    tags: ["animationCooldown"],
  },

  unlock_chat: {
    id: "unlock_chat",
    name: "Chat",
    description: "Unlock live chat events.",
    icon: "message-circle",
    category: "chat",
    kind: "unlock",
    scaling: {
      method: "flat",
      baseCost: 300,
    },
    maxLevel: 1,
    effects: [{ type: "enable_chat" }],
    tags: ["unlockChat"],
  },

  automod: {
    id: "automod",
    name: "AutoMod",
    description: "Reduce bad comment pressure.",
    icon: "shield",
    category: "chat",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 350,
      factor: 1.22,
    },
    unlockRequirements: [{ type: "chat_unlocked" }],
    effects: [
      {
        type: "multiply_economy",
        target: "badCommentRatePerViewer",
        amount: -0.1,
      },
    ],
    tags: ["badCommentReduction"],
  },

  chat_engagement: {
    id: "chat_engagement",
    name: "Chat Engagement",
    description: "Increase positive chat activity.",
    icon: "message-circle",
    category: "chat",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 400,
      factor: 1.22,
    },
    unlockRequirements: [{ type: "chat_unlocked" }],
    effects: [
      { type: "multiply_economy", target: "chatRatePerViewer", amount: 0.08 },
    ],
    tags: ["chatRate"],
  },

  gift_hype: {
    id: "gift_hype",
    name: "Gift Hype",
    description: "Increase gift event frequency.",
    icon: "gift",
    category: "conversion",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 500,
      factor: 1.24,
    },
    unlockRequirements: [{ type: "chat_unlocked" }],
    effects: [
      { type: "multiply_economy", target: "giftRatePerViewer", amount: 0.12 },
    ],
    tags: ["giftRate"],
  },

  unlock_pose_confident: {
    id: "unlock_pose_confident",
    name: "Confident Pose",
    description: "Unlock the confident pose animation.",
    icon: "sparkles",
    category: "animation",
    kind: "unlock",
    scaling: {
      method: "flat",
      baseCost: 650,
    },
    maxLevel: 1,
    animationId: "Pose_confident",
    unlockRequirements: [
      { type: "animation_unlocked", animationId: "Backflip" },
    ],
    effects: [{ type: "unlock_animation", animationId: "Pose_confident" }],
    tags: ["unlockAnimation"],
  },

  pose_confident_hype: {
    id: "pose_confident_hype",
    name: "Pose Practice",
    description: "Increase real viewers gained from Confident Pose.",
    icon: "zap",
    category: "animation",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 700,
      factor: 1.22,
    },
    animationId: "Pose_confident",
    unlockRequirements: [
      { type: "animation_unlocked", animationId: "Pose_confident" },
    ],
    effects: [
      {
        type: "multiply_animation_power",
        animationId: "Pose_confident",
        amount: 0.18,
      },
    ],
    tags: ["animationPower"],
  },

  pose_confident_cdr: {
    id: "pose_confident_cdr",
    name: "Pose Cooldown",
    description: "Reduce Confident Pose cooldown.",
    icon: "rotate-cw",
    category: "animation",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 850,
      factor: 1.25,
    },
    animationId: "Pose_confident",
    unlockRequirements: [
      { type: "animation_unlocked", animationId: "Pose_confident" },
    ],
    effects: [
      {
        type: "multiply_animation_cooldown",
        animationId: "Pose_confident",
        amount: 0.07,
        minMultiplier: 0.35,
      },
    ],
    tags: ["animationCooldown"],
  },

  unlock_aura_farmer: {
    id: "unlock_aura_farmer",
    name: "Aura Farmer",
    description: "Unlock an aura farming pose that attracts fresh viewers.",
    icon: "sparkles",
    category: "animation",
    kind: "unlock",
    scaling: {
      method: "flat",
      baseCost: 900,
    },
    maxLevel: 1,
    animationId: "AuraFarmer",
    unlockRequirements: [
      { type: "animation_unlocked", animationId: "Pose_confident" },
    ],
    effects: [{ type: "unlock_animation", animationId: "AuraFarmer" }],
    tags: ["unlockAnimation"],
  },

  aura_farmer_hype: {
    id: "aura_farmer_hype",
    name: "Aura Hype",
    description: "Make Aura Farmer pull in even more real people.",
    icon: "zap",
    category: "animation",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 980,
      factor: 1.23,
    },
    animationId: "AuraFarmer",
    unlockRequirements: [
      { type: "animation_unlocked", animationId: "AuraFarmer" },
    ],
    effects: [
      {
        type: "multiply_animation_power",
        animationId: "AuraFarmer",
        amount: 0.14,
      },
    ],
    tags: ["animationPower"],
  },

  aura_farmer_cdr: {
    id: "aura_farmer_cdr",
    name: "Aura Cooldown",
    description: "Reduce the cooldown on Aura Farmer.",
    icon: "rotate-cw",
    category: "animation",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 1060,
      factor: 1.24,
    },
    animationId: "AuraFarmer",
    unlockRequirements: [
      { type: "animation_unlocked", animationId: "AuraFarmer" },
    ],
    effects: [
      {
        type: "multiply_animation_cooldown",
        animationId: "AuraFarmer",
        amount: 0.06,
        minMultiplier: 0.45,
      },
    ],
    tags: ["animationCooldown"],
  },

  unlock_dance: {
    id: "unlock_dance",
    name: "Dance",
    description: "Unlock a dance routine for the stream.",
    icon: "clapperboard",
    category: "animation",
    kind: "unlock",
    scaling: {
      method: "flat",
      baseCost: 980,
    },
    maxLevel: 1,
    animationId: "Dance",
    unlockRequirements: [
      { type: "animation_unlocked", animationId: "Pose_confident" },
    ],
    effects: [{ type: "unlock_animation", animationId: "Dance" }],
    tags: ["unlockAnimation"],
  },

  dance_hype: {
    id: "dance_hype",
    name: "Dance Hype",
    description: "Make your dance pull in even more real viewers.",
    icon: "zap",
    category: "animation",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 1100,
      factor: 1.24,
    },
    animationId: "Dance",
    unlockRequirements: [{ type: "animation_unlocked", animationId: "Dance" }],
    effects: [
      {
        type: "multiply_animation_power",
        animationId: "Dance",
        amount: 0.16,
      },
    ],
    tags: ["animationPower"],
  },

  dance_cdr: {
    id: "dance_cdr",
    name: "Dance Cooldown",
    description: "Reduce how long it takes before you can dance again.",
    icon: "rotate-cw",
    category: "animation",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 1260,
      factor: 1.25,
    },
    animationId: "Dance",
    unlockRequirements: [{ type: "animation_unlocked", animationId: "Dance" }],
    effects: [
      {
        type: "multiply_animation_cooldown",
        animationId: "Dance",
        amount: 0.06,
        minMultiplier: 0.4,
      },
    ],
    tags: ["animationCooldown"],
  },

  auto_backflip: {
    id: "auto_backflip",
    name: "Auto Backflip",
    description: "Automatically use Backflip when ready.",
    icon: "rocket",
    category: "automation",
    kind: "unlock",
    scaling: {
      method: "flat",
      baseCost: 1200,
    },
    maxLevel: 1,
    animationId: "Backflip",
    unlockRequirements: [
      { type: "animation_unlocked", animationId: "Backflip" },
      { type: "powerup_level", powerupId: "backflip_cdr", level: 3 },
    ],
    effects: [{ type: "automate_animation", animationId: "Backflip" }],
    tags: ["automation"],
  },

  global_routine: {
    id: "global_routine",
    name: "Stream Routine",
    description: "Boost all unlocked animations slightly.",
    icon: "clapperboard",
    category: "animation",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 1400,
      factor: 1.26,
    },
    unlockRequirements: [
      { type: "animation_unlocked", animationId: "Pose_confident" },
    ],
    effects: [{ type: "multiply_unlocked_animation_power", amount: 0.08 }],
    tags: ["globalAnimationPower"],
  },

  unlock_hoodie: {
    id: "unlock_hoodie",
    name: "Hoodie",
    description: "Unlock and equip a top layer for the avatar.",
    icon: "shirt",
    category: "content",
    kind: "unlock",
    scaling: {
      method: "flat",
      baseCost: 900,
    },
    maxLevel: 1,
    clothingId: "hoodie",
    effects: [
      { type: "unlock_clothing", clothingId: "hoodie" },
      {
        type: "set_avatar_slots",
        slots: AvatarCatalog.clothing.hoodie,
      },
      {
        type: "add_economy",
        target: "realViewerFlowPerSecond",
        amount: 0.18,
      },
      {
        type: "add_economy",
        target: "realViewerRetention",
        amount: 0.1,
      },
    ],
    tags: ["unlockClothing"],
  },

  signature_fit: {
    id: "signature_fit",
    name: "Signature Fit",
    description: "Boost likes production while wearing clothing.",
    icon: "crown",
    category: "content",
    kind: "repeatable",
    scaling: {
      method: "exponential",
      baseCost: 1600,
      factor: 1.25,
    },
    unlockRequirements: [{ type: "likes", amount: 1000 }],
    effects: [
      {
        type: "multiply_economy",
        target: "likesPerSecondMultiplier",
        amount: 0.08,
      },
    ],
    tags: ["clothingBonus"],
  },

  retirement: {
    id: "retirement",
    name: "Retirement",
    description: "End the run and prestige.",
    icon: "crown",
    category: "prestige",
    kind: "prestige",
    scaling: {
      method: "flat",
      baseCost: 10000,
    },
    maxLevel: 1,
    unlockRequirements: [{ type: "likes", amount: 7500 }],
    effects: [],
    tags: ["prestige"],
  },
} satisfies Record<string, PowerupDefinition>;

export type PowerupId = keyof typeof POWERUPS;

export const POWERUP_LIST = Object.values(POWERUPS);
